<template>
  <el-dialog
    class="pto-form-modal"
    :title="modalTitle"
    :before-close="handleClose"
    :visible.sync="formModalOpened"
  >
    <el-alert
      title="Please only request PTO in 1/2 or full day increments."
      type="warning"
      :closable="false"
      show-icon
    >
    </el-alert>
    <br />
    <el-alert
      title="Saffron does not allow requests to span multiple weeks. Be sure to make multiple requests for longer PTOs."
      type="warning"
      :closable="false"
      show-icon
    >
    </el-alert>
    <br />
    <el-form
      ref="ptoForm"
      :model="formData"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >

      <el-form-item prop="category_id" label="Category">
        <el-select class="full-width" v-model.lazy.trim="formData.category_id" placeholder="Select category">
          <el-option
            v-for="(category, index) in categories"
            :key="index"
            :label="category.name"
            :value="category.id">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item prop="requested_offdays" label="Days">
        <el-select class="full-width" v-model.lazy.trim="formData.requested_offdays" placeholder="Select Days">
          <el-option
            v-for="(requested_offdays, index) in pto_types"
            :key="index"
            :label="requested_offdays"
            :value="requested_offdays">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item prop="starts_at" label="Start time">
        <el-col :span="11">
          <el-date-picker
            size="large"
            class="full-width"
            v-model="formData.starts_at"
            :type="datePickerType"
            align="right"
            placeholder="Start Date"
            default-time="9:00:00"
            format="MM/dd/yyyy hh:mm a">
          </el-date-picker>
        </el-col>
        <el-col class="line" align="center" :span="2">to</el-col>
        <el-col :span="11">
          <el-form-item prop="ends_at">
            <el-date-picker
              size="large"
              class="full-width"
              v-model="ptoEndDate"
              :type="datePickerType"
              align="right"
              placeholder="End Date"
              :editable="false"
              format="MM/dd/yyyy hh:mm a">
            </el-date-picker>
          </el-form-item>
        </el-col>
      </el-form-item>

      <el-form-item prop="comments_attributes.text" label="Note">
        <el-input type="textarea" v-model.lazy="formData.comments_attributes.text" placeholder="Your note..."></el-input>
      </el-form-item>

    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="submitForm()" :loading="loading">Save</el-button>
    </span>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import { mapActions, mapGetters } from 'vuex';
  import _ from 'lodash';
  import validations from '@/utils/validations';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import formPopulator from '@/mixins/form_populator';

  export default {
    name: 'PtoFormModal',
    mixins: [formPopulator],
    mounted() {
      this.loadingCategories = true;
      this.fetchCategories({ type: 'PtoCategory', scope: 'active' })
        .then(() => {
          this.loadingCategories = false;
        });
    },
    data() {
      return {
        loadingCategories: false,
        formData: {
          category_id: '',
          pto_type: '',
          requested_offdays: '',
          starts_at: '',
          ends_at: '',
          comments_attributes: {
            text: '',
          },
        },
        pto_types: [0.5, ..._.range(1, 6, 1)],
        rules: {
          category_id: [
            { ...validations.required, type: 'integer' },
          ],
          requested_offdays: [
            { ...validations.required, type: 'number' },
          ],
          starts_at: [
            { ...validations.required, type: 'object' },
          ],
        },
        pickerOptions: {
          disabledDate(date) {
            return moment().isSameOrAfter(date);
          },
        },
      };
    },
    computed: {
      ...mapGetters('entities/ptos', ['creating', 'ptoFromContext']),

      formModalOpened: {
        get() {
          return this.$store.getters['entities/ptos/formModalOpened'];
        },
        set(newValue) {
          if (!newValue) {
            this.closeFormModal();
          }
        },
      },
      datePickerType() {
        let type = null;
        if (this.formData.requested_offdays === 0.5) {
          type = 'datetime';
        } else {
          if (this.formData.starts_at !== '') {
            this.formData.starts_at = moment(this.formData.starts_at).set({ hours: 9, minute: 0 });
          }
          type = 'date';
        }
        return type;
      },
      ptoEndDate: {
        get() {
          let endsAt = null;
          if (this.formData.starts_at !== '' && this.formData.requested_offdays !== '') {
            endsAt = moment(this.formData.starts_at);
            const count = this.formData.requested_offdays;
            if (count > 0.5) {
              endsAt = endsAt.add(count - 1, 'day').add(8, 'hour');
            } else {
              endsAt = endsAt.add(4, 'hour');
            }
          }
          this.formData.ends_at = endsAt;
          return endsAt;
        },
        set() {},
      },
      modalTitle() {
        return this.ptoFromContext ? 'Edit PTO' : 'Add New PTO';
      },

      defaultCategory() {
        return this.$store.getters['entities/categories/getDefaultCategory']('PtoCategory');
      },

      categories() {
        return this.$store.getters['entities/categories/query']()
          .where('type', 'PtoCategory')
          .get();
      },

      loading() {
        return this.creating || this.updating;
      },

      updating() {
        return this.ptoFromContext ? this.$store.getters['entities/ptos/isUpdating'](this.ptoFromContext.id) : false;
      },
    },
    methods: {
      ...mapActions('entities/ptos', ['updatePto', 'createPto', 'closeFormModal', 'setContext']),
      ...mapActions('entities/categories', ['fetchCategories']),

      submitForm() {
        this.$refs.ptoForm.validate((valid) => {
          if (valid) {
            if (this.ptoFromContext) {
              this.updatePto({ id: this.ptoFromContext.id, data: this.formData })
                .then((response) => {
                  if (response.status) {
                    showSuccessMessage('Pto has been edited successfully');
                    this.handleClose();
                  } else {
                    showErrorMessage(response.error);
                  }
                });
            } else {
              this.createPto(this.formData)
                .then((response) => {
                  if (response.status) {
                    showSuccessMessage('Pto has been created successfully');
                    this.handleClose();
                  } else {
                    showErrorMessage(response.error);
                  }
                });
            }
          } else {
            showErrorMessage(this.$t('form.messages.notValid'));

            return false;
          }

          return true;
        });
      },
      clearForm() {
        this.$refs.ptoForm.resetFields();
      },

      handleClose() {
        this.closeFormModal();
        this.setContext(null);
      },

    },
    watch: {
      ptoFromContext: {
        handler(newObject) {
          if (newObject) {
            this.populateFormWithModel(newObject, 'formData');
          } else {
            this.clearForm();
          }
        },
        deep: true,
      },
      defaultCategory(newValue) {
        if (newValue) {
          this.formData.category_id = newValue.id;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .pto-form-modal {
    .line {
      text-align: center;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pto/PtoFormModal.vue